/* eslint-disable react/require-default-props */
import React, {
  useContext, useMemo, useEffect, useCallback, useState,
} from 'react';
import _ from 'underscore';
import classNames from 'classnames';

import {

  Coachmark, TeachingBubbleContent, Callout, Text, Stack, IconButton, DirectionalHint, DefaultButton, PrimaryButton,
} from '@bingads-webui-react/fabric-control';
import { useId } from '@fluentui/react-hooks';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withLock, keyVault, priorityLevel } from '@bingads-webui-react/popup-manager';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Scenario, Activity, withActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { withUserGovernance } from './with-user-governance';
import { getTeachingBubbleUserGovernanceConfig } from './utils';
import { getStyles, styles } from './styles';
import { useLogger } from './logger';
import type {
  IActivity, ITeachingBubbleActionHandler, INoArgHandler, IUserGovernanceConfig,
} from './types';

// update state to force render
const useForceUpdate = () => {
  const [, setValue] = useState(0);
  return () => setValue(value => value + 1);
};

export type TeachingBubbleTarget = React.ComponentProps<typeof Callout>['target'];
export type TeachingBubbleDirectionalHint = React.ComponentProps<typeof Callout>['directionalHint'];

export type TeachingBubbleInnerProps = {
  isOneStepTeachingBubble?: boolean;
  classes?: {
    teachingBubbleContent: string;
  };
  activity?: IActivity;
  featureName: string;
  subFeatureName?: string;
  targetElement?: TeachingBubbleTarget;
  title?: string;
  content: React.ReactNode;
  primaryButtonText: string;
  secondaryButtonText?: string;
  onDone: ITeachingBubbleActionHandler;
  onDismiss?: ITeachingBubbleActionHandler;
  onClose: ITeachingBubbleActionHandler;
  isLightDismissSupported?: boolean;
  directionalHint?: TeachingBubbleDirectionalHint;
  footerText?: string;
  skipOnDoneGovernance?: boolean;
  doNotLayer?: boolean;
  className?: string;
  isSecondaryButtonHidden?: boolean;
  shouldRerender?: boolean;
};

export const TeachingBubbleInner: React.FC<TeachingBubbleInnerProps> = ({
  isOneStepTeachingBubble,
  classes,
  activity,
  featureName,
  subFeatureName,
  targetElement,
  title,
  content,
  primaryButtonText,
  secondaryButtonText,
  footerText,

  // primary button
  onDone,

  // secondary button
  onDismiss,

  // close button
  onClose,

  isLightDismissSupported,
  directionalHint,
  skipOnDoneGovernance,
  doNotLayer,
  className,
  isSecondaryButtonHidden,
  shouldRerender,
}: TeachingBubbleInnerProps) => {
  const { logTrace } = useLogger(activity, featureName, subFeatureName);
  const i18n = useContext(I18nContext);
  const forceUpdate = useForceUpdate();

  const labelId = useId('callout-label');
  const descriptionId = useId('callout-description');

  const onDoneButtonClick = useCallback(() => {
    logTrace('done button click');
    onDone(skipOnDoneGovernance);
  }, [logTrace, onDone, skipOnDoneGovernance]);

  const onDismissButtonClick = useCallback(() => {
    logTrace('dismiss button click');
    onDismiss();
  }, [logTrace, onDismiss]);

  const onCloseButtonClick = useCallback(() => {
    logTrace('close button click');
    onClose();
  }, [logTrace, onClose]);

  const onCalloutDismiss = useCallback((event: Parameters<NonNullable<React.ComponentProps<typeof Callout>['onDismiss']>>[0]) => {
    if (event && 'keyCode' in event && event.keyCode === 27) { // Escape
      logTrace('keyboard ESC click');
      onDismiss();
    } else if (isLightDismissSupported) {
      logTrace('outside click');
      onDismiss(true /* skipOnDismissGovernance */);
    }
  }, [isLightDismissSupported, logTrace, onDismiss]);

  const preventDismissOnEvent = useCallback((event: Parameters<NonNullable<
    React.ComponentProps<typeof Callout>['preventDismissOnEvent']
    >>[0]) => {
    if (event && event.type === 'scroll') {
      // When grid table render, it will trigger resize, which will cause teaching bubble dismiss.
      // Add this check to prevent this dismiss.
      return true;
    }
    return false;
  }, []);

  const footerContent = useMemo(() => (
    <>
      <DefaultButton text={primaryButtonText} onClick={onDoneButtonClick} />
      {!isSecondaryButtonHidden
        && <PrimaryButton text={secondaryButtonText} onClick={onDismissButtonClick} />}
    </>
  ), [primaryButtonText, onDoneButtonClick, isSecondaryButtonHidden, secondaryButtonText, onDismissButtonClick]);

  useEffect(() => {
    logTrace(isOneStepTeachingBubble
      ? 'feature level teaching bubble render' : 'teaching bubble mount');
  }, [isOneStepTeachingBubble, logTrace]);

  useEffect(() => {
    if (shouldRerender) {
      forceUpdate();
    }
  // Only depend on shouldRerender value
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRerender]);

  return (
    isOneStepTeachingBubble
      ? (
        <Callout
          ariaLabelledBy={labelId}
          ariaDescribedBy={descriptionId}
          role="dialog"
          className={classNames(['teaching-bubble-content', className, styles.callout])}
          target={targetElement}
          directionalHint={directionalHint}
          doNotLayer={doNotLayer}
          onDismiss={onCalloutDismiss}
          preventDismissOnEvent={preventDismissOnEvent}
        >
          <Text block variant="xLarge" className={classNames(['ms-TeachingBubble-headline', styles.title])} id={labelId}>
            {title}
          </Text>
          <Stack className={classNames(['ms-TeachingBubble-body', styles.content])}>
            {content}
          </Stack>
          <Stack horizontal verticalAlign="center" className={classNames(['ms-TeachingBubble-footer', styles.footer])}>
            <Stack.Item>
              <DefaultButton
                className={styles.defaultBtn}
                text={primaryButtonText}
                title={primaryButtonText}
                onClick={onDoneButtonClick}
              />
            </Stack.Item>
            {!isSecondaryButtonHidden
            && (
            <Stack.Item>
              <PrimaryButton
                className={styles.primaryBtn}
                text={secondaryButtonText}
                title={secondaryButtonText}
                onClick={onDismissButtonClick}
              />
            </Stack.Item>
            )}
            {
            footerText
            && (
            <Stack.Item>
              <Text className={classNames('ms-TeachingBubble-footer-text', styles.footerText)}>{footerText}</Text>
            </Stack.Item>
            )
          }
          </Stack>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            ariaLabel={i18n.getString(_TL_('Close'))}
            onClick={onCloseButtonClick}
            style={{ position: 'absolute', top: '0', right: '0' }}
            className={classNames(['teaching-bubble-close-button', styles.closeBtn])}
          />
        </Callout>
      )
      : (
        <div
          className={classNames(['teaching-bubble-content', classes?.teachingBubbleContent])}
        >
          <TeachingBubbleContent
            headline={title}
            hasCloseButton
            closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
            onDismiss={onCloseButtonClick}
            styles={styles}
            footerContent={footerContent}
          >
            {content}
          </TeachingBubbleContent>
        </div>
      )
  );
};

TeachingBubbleInner.defaultProps = {
  isOneStepTeachingBubble: false,
  activity: {
    trace: _.noop,
    error: _.noop,
  },
  isLightDismissSupported: false,
  directionalHint: DirectionalHint.bottomLeftEdge,
  footerText: null,
  subFeatureName: undefined,
  skipOnDoneGovernance: false,
  doNotLayer: false,
  className: undefined,
  isSecondaryButtonHidden: false,
  shouldRerender: false,
};

const StyledTeachingBubbleInnerWithoutLock = _.compose(
  withActivityContext,
  withUserGovernance,
  (WrappedComponent: React.ComponentType<TeachingBubbleInnerProps>) => withDefaultStyles(WrappedComponent, getStyles)
)(TeachingBubbleInner);

// We should ensure withUserGovernance HOC wrap withLock HOC.
const StyledTeachingBubbleInner = _.compose(
  withActivityContext,
  withUserGovernance,
  (WrappedComponent: React.ComponentType<TeachingBubbleInnerProps>) => withLock(WrappedComponent, {
    lockKey: keyVault.teachingBubble,
    priority: priorityLevel.teachingBubble,
    autoClose: true,
  }),
  (WrappedComponent: React.ComponentType<TeachingBubbleInnerProps>) => withDefaultStyles(WrappedComponent, getStyles)
)(TeachingBubbleInner);

export type TeachingBubbleProps = Omit<
TeachingBubbleInnerProps,
'activity' | 'classes' | 'isOneStepTeachingBubble'
> & {
  userGovernanceConfig?: IUserGovernanceConfig;
};

export const TeachingBubble = (props: TeachingBubbleProps) => (
  <Scenario name="TeachingBubble">
    <Activity name="TeachingBubble">
      <StyledTeachingBubbleInner
        userGovernanceConfig={getTeachingBubbleUserGovernanceConfig()}
        isOneStepTeachingBubble
        {...props}
      />
    </Activity>
  </Scenario>
);

export type TeachingBubbleWithoutLockProps = {
  userGovernanceConfig?: IUserGovernanceConfig;
} & TeachingBubbleInnerProps;

export const TeachingBubbleWithoutLock = (props: TeachingBubbleWithoutLockProps) => (
  <Scenario name="TeachingBubble">
    <Activity name="TeachingBubble">
      <StyledTeachingBubbleInnerWithoutLock
        userGovernanceConfig={props.userGovernanceConfig}
        isOneStepTeachingBubble
        {...props}
      />
    </Activity>
  </Scenario>
);

TeachingBubbleWithoutLock.defaultProps = {
  userGovernanceConfig: getTeachingBubbleUserGovernanceConfig(),
};

type UnstyledTeachingBubbleProps = {
  activity?: IActivity;
  classes: {
    teachingBubbleContent: string;
  };
  featureName: string;
  subFeatureName?: string;
  targetElement: React.ComponentProps<typeof Coachmark>['target'];
  title?: string;
  content: React.ReactNode;
  primaryButtonText: string;
  secondaryButtonText?: string;
  onDone: ITeachingBubbleActionHandler;
  onDismiss?: ITeachingBubbleActionHandler;
  onClose: ITeachingBubbleActionHandler;
  isLightDismissSupported?: boolean;
  onTeachingBubbleOpen?: INoArgHandler;
  directionalHint?: TeachingBubbleDirectionalHint;
  rightOffset?: number;
};

const UnstyledTeachingBubble = ({
  activity,
  classes,
  featureName,
  subFeatureName,
  title,
  content,
  primaryButtonText,
  secondaryButtonText,
  onDone, // primary button
  onDismiss, // secondary button
  onClose, // close button
  isLightDismissSupported,
  onTeachingBubbleOpen,
  targetElement,
  directionalHint,
  rightOffset,
}: UnstyledTeachingBubbleProps) => {
  const { logTrace } = useLogger(activity, featureName, subFeatureName);

  const positioningContainerProps = useMemo<NonNullable<
  React.ComponentProps<typeof Coachmark>['positioningContainerProps']
  >>(() => ({
    offsetFromTarget: 0,
    directionalHint,
  }), [directionalHint]);

  const onAnimationOpenEnd = useCallback(() => {
    logTrace('teaching bubble show (extend)');
    onTeachingBubbleOpen();
  }, [logTrace, onTeachingBubbleOpen]);

  useEffect(() => {
    logTrace('coach mark show');
  }, [logTrace]);

  return (
    <Coachmark
      className={classNames('teaching-bubble', featureName)}
      target={targetElement}
      positioningContainerProps={positioningContainerProps}
      styles={{ root: { right: rightOffset } }}
      persistentBeak
      preventFocusOnMount
      // teaching bubble will be extend when the coachmark is closed
      onAnimationOpenEnd={onAnimationOpenEnd}
      delayBeforeCoachmarkAnimation={0}
      delayBeforeMouseOpen={0}
    >
      <TeachingBubbleInner
        activity={activity}
        classes={classes}
        featureName={featureName}
        subFeatureName={subFeatureName}
        title={title}
        content={content}
        primaryButtonText={primaryButtonText}
        secondaryButtonText={secondaryButtonText}
        onDone={onDone}
        onDismiss={onDismiss}
        onClose={onClose}
        isLightDismissSupported={isLightDismissSupported}
      />
    </Coachmark>
  );
};

UnstyledTeachingBubble.defaultProps = {
  activity: {
    trace: _.noop,
    error: _.noop,
  },
  isLightDismissSupported: false,
  onTeachingBubbleOpen: _.noop,
  directionalHint: DirectionalHint.bottomLeftEdge,
  rightOffset: -15,
};

const StyledTeachingBubble = _.compose(
  withActivityContext,
  withUserGovernance,
  (WrappedComponent: React.ComponentType<UnstyledTeachingBubbleProps>) => withLock(WrappedComponent, {
    lockKey: keyVault.teachingBubble,
    priority: priorityLevel.teachingBubble,
    autoClose: true,
  }),
  (WrappedComponent: React.ComponentType<UnstyledTeachingBubbleProps>) => withDefaultStyles(WrappedComponent, getStyles)
)(UnstyledTeachingBubble);

export type TeachingBubbleWithCoachmarkProps = Omit<
UnstyledTeachingBubbleProps,
'activity' | 'classes'
> & {
  userGovernanceConfig?: IUserGovernanceConfig;
};

export const TeachingBubbleWithCoachmark = (props: TeachingBubbleWithCoachmarkProps) => (
  <Scenario name="TeachingBubble">
    <Activity name="TeachingBubble">
      <StyledTeachingBubble
        userGovernanceConfig={getTeachingBubbleUserGovernanceConfig()}
        {...props}
      />
    </Activity>
  </Scenario>
);
